import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/site/Navbar";
import { ProjectForm } from "@/components/admin/ProjectForm";
import { EnquiriesPanel } from "@/components/admin/EnquiriesPanel";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const ProjectRow = ({ p, onEdit, onDelete }) => (
  <div data-testid={`admin-project-${p.slug}`} className="flex flex-col gap-4 rounded-3xl bg-white p-4 ring-1 ring-slate-900/5 sm:flex-row sm:items-center">
    <img src={p.cover_image} alt="" className="h-20 w-full rounded-2xl bg-[#F4F1EA] object-contain sm:w-32" />
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-display text-lg font-extrabold">{p.title}</h3>
        <span className="rounded-full px-2.5 py-0.5 text-xs font-bold text-white" style={{ background: p.accent }}>{p.category}</span>
        {!p.published && <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-bold text-slate-700">Draft</span>}
      </div>
      <p className="mt-1 line-clamp-1 text-sm text-slate-600">{p.summary}</p>
    </div>
    <div className="flex gap-2">
      {p.published && <Link to={`/work/${p.slug}`} target="_blank" aria-label="View" className="grid h-10 w-10 place-items-center rounded-full bg-slate-100 hover:bg-slate-200"><ExternalLink className="h-4 w-4" /></Link>}
      <button data-testid={`admin-edit-${p.slug}`} aria-label="Edit" onClick={() => onEdit(p)} className="grid h-10 w-10 place-items-center rounded-full bg-[#EFF6FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white"><Pencil className="h-4 w-4" /></button>
      <button data-testid={`admin-delete-${p.slug}`} aria-label="Delete" onClick={() => onDelete(p)} className="grid h-10 w-10 place-items-center rounded-full bg-[#FFF1F2] text-[#BE123C] hover:bg-[#BE123C] hover:text-white"><Trash2 className="h-4 w-4" /></button>
    </div>
  </div>
);

export default function AdminDashboard() {
  const { user, check, logout } = useAuth();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, e] = await Promise.all([api.get("/admin/projects"), api.get("/admin/enquiries")]);
      setProjects(Array.isArray(p.data) ? p.data : []);
      setEnquiries(Array.isArray(e.data) ? e.data : []);
    } catch (ex) { toast.error(errorText(ex)); }
  }, []);

  useEffect(() => { if (user === null) check(); }, [user, check]);
  useEffect(() => { if (user) load(); }, [user, load]);

  if (user === null) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-8 w-8 animate-spin text-[#0055FF]" /></div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const save = async (data) => {
    try {
      if (editing?.id) await api.put(`/admin/projects/${editing.id}`, data);
      else await api.post("/admin/projects", data);
      toast.success("Project saved");
      setEditing(null); load();
    } catch (ex) { toast.error(errorText(ex)); }
  };

  const doDelete = async () => {
    const { kind, item } = confirm;
    try {
      await api.delete(kind === "project" ? `/admin/projects/${item.id}` : `/admin/enquiries/${item.id}`);
      toast.success("Deleted"); load();
    } catch (ex) { toast.error(errorText(ex)); }
    setConfirm(null);
  };

  const setStatus = async (e, status) => {
    try { await api.patch(`/admin/enquiries/${e.id}`, { status }); load(); } catch (ex) { toast.error(errorText(ex)); }
  };

  const cats = Array.from(new Set(projects.map((p) => p.category)));
  const tabBtn = (k, label) => (
    <button data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-5 py-2.5 text-sm font-bold ${tab === k ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-100"}`}>{label}</button>
  );

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAF9F5]">
      <header className="border-b border-slate-900/10 bg-white">
        <div className="container-x flex items-center justify-between py-4">
          <Logo />
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-slate-600 sm:inline">{user.email}</span>
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 text-sm font-bold hover:bg-slate-200"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </header>
      <main className="container-x py-10">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <h1 className="font-display text-4xl font-extrabold">Dashboard</h1>
          {tab === "projects" && <button data-testid="admin-add-project" onClick={() => setEditing({})} className="flex items-center justify-center gap-2 rounded-full bg-[#0055FF] px-6 py-3 font-bold text-white hover:bg-[#0043cc]"><Plus className="h-5 w-5" /> Add project</button>}
        </div>
        <div className="mt-6 flex gap-2">{tabBtn("projects", `Projects (${projects.length})`)}{tabBtn("enquiries", `Enquiries (${enquiries.length})`)}</div>
        <div className="mt-6">
          {tab === "projects" ? (
            <div data-testid="admin-projects-list" className="space-y-3">
              {projects.map((p) => <ProjectRow key={p.id} p={p} onEdit={setEditing} onDelete={(x) => setConfirm({ kind: "project", item: x })} />)}
            </div>
          ) : (
            <EnquiriesPanel items={enquiries} onStatus={setStatus} onDelete={(x) => setConfirm({ kind: "enquiry", item: x })} />
          )}
        </div>
      </main>
      {editing && <ProjectForm open project={editing.id ? editing : null} categories={cats} onClose={() => setEditing(null)} onSave={save} />}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {confirm?.kind}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes "{confirm?.item?.title || confirm?.item?.name}". This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete" onClick={doDelete} className="bg-[#BE123C] hover:bg-[#9f1239]">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
